import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, XCircle, AlertTriangle, Clock, TestTube, GitCompare } from "lucide-react";

interface ValidationMetrics {
  temporalAccuracy: boolean;
  dataConsistency: boolean;
  performanceMs: number;
  patternValidation: boolean;
}

interface ValidationResult {
  testId: string;
  botId: string;
  success: boolean;
  errors: string[];
  warnings: string[];
  metrics: ValidationMetrics;
  testDetails: {
    fullSequenceLength: number;
    currentPosition: number;
    extractedPatterns: string[];
    comparisonWithLegacy?: {
      legacyPatterns: string[];
      newPatterns: string[];
      differences: string[];
    };
  };
}

export function PatternValidationTester() {
  const [botId, setBotId] = useState('');
  const [testMode, setTestMode] = useState<'validation' | 'comparison'>('validation');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runValidationTest = async () => {
    if (!botId.trim()) {
      setError('Please enter a bot ID');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('pattern-validation-test', {
        body: { botId: botId.trim(), testMode }
      });

      if (functionError) throw functionError;
      
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Test failed');
      console.error('Validation test error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (success: boolean) => success ? 'text-green-600' : 'text-red-600';
  const getStatusIcon = (success: boolean) => success ? CheckCircle : XCircle;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          Pattern Validation Tester
        </CardTitle>
        <CardDescription>
          Test and validate "Ending At Position" logic implementation for trading pattern analysis
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Test Configuration */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Enter Bot ID (e.g., sdsd bot)"
                value={botId}
                onChange={(e) => setBotId(e.target.value)}
                className="w-full"
              />
            </div>
            <Tabs value={testMode} onValueChange={(value) => setTestMode(value as 'validation' | 'comparison')}>
              <TabsList className="grid w-fit grid-cols-2">
                <TabsTrigger value="validation">Validation</TabsTrigger>
                <TabsTrigger value="comparison">Comparison</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button onClick={runValidationTest} disabled={loading} className="min-w-fit">
              {loading ? (
                <Clock className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              Run Test
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Results Display */}
          {result && (
            <div className="space-y-6">
              {/* Test Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${getStatusColor(result.success)}`}>
                    {React.createElement(getStatusIcon(result.success), { className: "h-5 w-5" })}
                    Test {result.success ? 'PASSED' : 'FAILED'}
                  </CardTitle>
                  <CardDescription>
                    Test ID: {result.testId} | Bot: {result.botId} | Duration: {result.metrics.performanceMs}ms
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(result.metrics.temporalAccuracy)}`}>
                        {result.metrics.temporalAccuracy ? '✓' : '✗'}
                      </div>
                      <div className="text-sm text-muted-foreground">Temporal Accuracy</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(result.metrics.dataConsistency)}`}>
                        {result.metrics.dataConsistency ? '✓' : '✗'}
                      </div>
                      <div className="text-sm text-muted-foreground">Data Consistency</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(result.metrics.patternValidation)}`}>
                        {result.metrics.patternValidation ? '✓' : '✗'}
                      </div>
                      <div className="text-sm text-muted-foreground">Pattern Validation</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${result.metrics.performanceMs < 5000 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {result.metrics.performanceMs}ms
                      </div>
                      <div className="text-sm text-muted-foreground">Performance</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Test Details */}
              <Tabs defaultValue="details">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="patterns">Patterns</TabsTrigger>
                  {result.testDetails.comparisonWithLegacy && (
                    <TabsTrigger value="comparison">Comparison</TabsTrigger>
                  )}
                  <TabsTrigger value="issues">Issues</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Test Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <strong>Full Sequence Length:</strong> {result.testDetails.fullSequenceLength}
                        </div>
                        <div>
                          <strong>Current Position:</strong> {result.testDetails.currentPosition}
                        </div>
                        <div>
                          <strong>Extracted Patterns:</strong> {result.testDetails.extractedPatterns.length}
                        </div>
                        <div>
                          <strong>Test Mode:</strong> {testMode}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="patterns" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Extracted Patterns</CardTitle>
                      <CardDescription>
                        Patterns extracted using "Ending At Position" logic
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {result.testDetails.extractedPatterns.map((pattern, index) => (
                          <Badge key={index} variant="outline" className="justify-center">
                            {pattern}
                          </Badge>
                        ))}
                      </div>
                      {result.testDetails.extractedPatterns.length === 0 && (
                        <div className="text-center text-muted-foreground py-4">
                          No patterns extracted
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {result.testDetails.comparisonWithLegacy && (
                  <TabsContent value="comparison" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <GitCompare className="h-5 w-5" />
                          Legacy vs New Logic Comparison
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold mb-2">Legacy "From End" Patterns</h4>
                            <div className="space-y-1">
                              {result.testDetails.comparisonWithLegacy.legacyPatterns.map((pattern, index) => (
                                <Badge key={index} variant="secondary">
                                  {pattern}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2">New "Ending At Position" Patterns</h4>
                            <div className="space-y-1">
                              {result.testDetails.comparisonWithLegacy.newPatterns.map((pattern, index) => (
                                <Badge key={index} variant="default">
                                  {pattern}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        {result.testDetails.comparisonWithLegacy.differences.length > 0 && (
                          <div className="mt-6">
                            <h4 className="font-semibold mb-2 text-yellow-600">Differences Found</h4>
                            <div className="space-y-1">
                              {result.testDetails.comparisonWithLegacy.differences.map((diff, index) => (
                                <div key={index} className="text-sm text-muted-foreground">
                                  {diff}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                <TabsContent value="issues" className="space-y-4">
                  {result.errors.length > 0 && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-semibold mb-2">Errors ({result.errors.length})</div>
                        <ul className="list-disc list-inside space-y-1">
                          {result.errors.map((error, index) => (
                            <li key={index} className="text-sm">{error}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {result.warnings.length > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-semibold mb-2">Warnings ({result.warnings.length})</div>
                        <ul className="list-disc list-inside space-y-1">
                          {result.warnings.map((warning, index) => (
                            <li key={index} className="text-sm">{warning}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {result.errors.length === 0 && result.warnings.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-600" />
                      No issues found
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}